<template>
  <div class="gs-support-info">
    <div class="gs-support-info__head">
      <div>
        <h3>Support Info</h3>
        <p>Add the support channels customers can use after purchase.</p>
      </div>
      <button type="button" @click="openChannelModal">
        <span>+</span>
        Add New Support Channel
      </button>
    </div>

    <div v-if="supportChannels.length" class="gs-support-info__list">
      <article
        v-for="channel in supportChannels"
        :key="channel.id"
        class="gs-support-info__channel"
      >
        <div>
          <strong>{{ channel.name }}</strong>
          <span>{{ supportTypeLabel(channel.type) }}</span>
        </div>
        <code>{{ channel.value }}</code>
        <button type="button" @click="removeChannel(channel.id)">
          Remove
        </button>
      </article>
    </div>

    <div v-else class="gs-support-info__empty">
      No support channels added yet.
    </div>

    <gs-modal v-model="showChannelModal" size="lg" hide-header>
    <aside class="gs-support-info__modal">
      <div class="gs-support-info__modal-head">
        <button type="button" @click="closeChannelModal">
          <gs-icon name="arrow-left" size="16"></gs-icon>
        </button>
        <h3>Add new support channel</h3>
        <gs-button type="button" variant="primary" @click="saveChannel">
          Save
        </gs-button>
      </div>

      <label>
        <span>Name</span>
        <input v-model="draftChannel.name" type="text" placeholder="Support channel name" />
      </label>

      <label>
        <span>Support Type</span>
        <select v-model="draftChannel.type">
          <option
            v-for="option in supportTypeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.text }}
          </option>
        </select>
      </label>

      <label>
        <span>{{ supportValueLabel }}</span>
        <input
          v-model="draftChannel.value"
          :type="supportInputType"
          :placeholder="supportValuePlaceholder"
        />
      </label>
    </aside>
    </gs-modal>
  </div>
</template>

<script>
export default {
  props: {
    is_support_enabled: {
      default: false,
    },
    support_info_type: {
      default: 1,
    },
    support_info_value: {
      default: '',
    },
    validation: {
      required: true,
    },
  },
  data() {
    return {
      showChannelModal: false,
      supportChannels: this.parseSupportChannels(),
      draftChannel: this.emptyChannel(),
    };
  },
  computed: {
    supportTypeOptions() {
      return [
        { value: 'email', text: 'Support email' },
        { value: 'helpdesk_url', text: 'Help desk URL' },
        { value: 'phone', text: 'Phone number' },
        { value: 'chat_widget_url', text: 'Chat Widget URL' },
        { value: 'knowledge_base', text: 'Knowledge base' },
      ];
    },
    supportValueLabel() {
      const labels = {
        email: 'Enter support email',
        helpdesk_url: 'Enter help desk URL',
        phone: 'Enter phone number',
        chat_widget_url: 'Enter chat widget URL',
        knowledge_base: 'Enter knowledge base URL',
      };

      return labels[this.draftChannel.type] || 'Enter support value';
    },
    supportValuePlaceholder() {
      const placeholders = {
        email: 'email@example.com',
        helpdesk_url: 'https://support.example.com',
        phone: '+1 555 123 4567',
        chat_widget_url: 'https://chat.example.com/widget',
        knowledge_base: 'https://help.example.com',
      };

      return placeholders[this.draftChannel.type] || '';
    },
    supportInputType() {
      return this.draftChannel.type === 'email' ? 'email' : 'text';
    },
  },
  watch: {
    supportChannels: {
      deep: true,
      handler() {
        this.persistChannels();
      },
    },
  },
  methods: {
    emptyChannel() {
      return {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        name: '',
        type: 'email',
        value: '',
      };
    },
    parseSupportChannels() {
      if (!this.support_info_value) {
        return [];
      }

      try {
        const parsed = JSON.parse(this.support_info_value);
        if (parsed && Array.isArray(parsed.channels)) {
          return parsed.channels;
        }
      } catch (e) {
        // Legacy products stored one support value. Convert it into the new list shape.
      }

      if (!this.is_support_enabled) {
        return [];
      }

      return [
        {
          id: 'legacy-support-channel',
          name: this.support_info_type == 2 ? 'Email support' : 'Help desk',
          type: this.support_info_type == 2 ? 'email' : 'helpdesk_url',
          value: this.support_info_value,
        },
      ];
    },
    supportTypeLabel(type) {
      const option = this.supportTypeOptions.find((item) => item.value === type);
      return option ? option.text : 'Support channel';
    },
    openChannelModal() {
      this.draftChannel = this.emptyChannel();
      this.showChannelModal = true;
    },
    closeChannelModal() {
      this.showChannelModal = false;
    },
    saveChannel() {
      if (!this.draftChannel.name || !this.draftChannel.value) {
        return;
      }

      this.supportChannels.push({ ...this.draftChannel });
      this.showChannelModal = false;
    },
    removeChannel(id) {
      this.supportChannels = this.supportChannels.filter((channel) => channel.id !== id);
    },
    persistChannels() {
      const hasChannels = this.supportChannels.length > 0;
      const payload = hasChannels
        ? JSON.stringify({ version: 2, channels: this.supportChannels })
        : null;

      this.$emit('update-model', { is_support_enabled: hasChannels ? 1 : 0 });
      this.$emit('update-model', { support_info_type: 3 });
      this.$emit('update-model', { support_info_value: payload });

      if (this.validation && this.validation.localModel.support_info_value) {
        this.validation.localModel.support_info_value.$touch();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.gs-support-info {
  position: relative;
}

.gs-support-info__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}

.gs-support-info__head h3,
.gs-support-info__modal h3 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 16px;
  font-weight: 650;
  line-height: 1.35;
}

.gs-support-info__head p {
  margin: 4px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.4;
}

.gs-support-info__head button,
.gs-support-info__modal-head .is-save {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 42px;
  padding: 0 16px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  background: var(--gs-primary);
  border: 1px solid var(--gs-primary);
  border-radius: var(--gs-radius-md);
  cursor: pointer;
}

.gs-support-info__head button span {
  display: inline-grid;
  place-items: center;
  width: 22px;
  height: 22px;
  font-size: 18px;
  border-radius: 999px;
  background: rgba(255, 255, 255, .22);
}

.gs-support-info__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.gs-support-info__channel {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 12px;
  align-items: center;
  min-height: 64px;
  padding: 14px 16px;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
}

.gs-support-info__channel strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
}

.gs-support-info__channel span,
.gs-support-info__channel code {
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.35;
}

.gs-support-info__channel code {
  padding: 6px 10px;
  background: var(--gs-surface-subtle);
  border-radius: var(--gs-radius-sm);
}

.gs-support-info__channel button {
  min-height: 34px;
  padding: 0 12px;
  color: var(--gs-primary);
  font-size: 13px;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #ffb4c5;
  border-radius: var(--gs-radius-md);
  cursor: pointer;
}

.gs-support-info__empty {
  padding: 16px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  background: var(--gs-surface-subtle);
  border: 1px dashed var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-support-info__modal {
  width: 100%;
  min-height: 420px;
  padding: 28px;
  background: #ffffff;
  border-radius: 0;
  box-shadow: none;
}

.gs-support-info__modal-head {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  margin-bottom: 24px;
}

.gs-support-info__modal-head button:not(.is-save) {
  width: 36px;
  height: 36px;
  color: var(--gs-muted-foreground);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gs-support-info__modal label {
  display: block;
  margin-bottom: 20px;
}

.gs-support-info__modal label span {
  display: block;
  margin-bottom: 8px;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
}

.gs-support-info__modal input,
.gs-support-info__modal select {
  width: 100%;
  min-height: 42px;
  padding: 0 12px;
  color: var(--gs-foreground);
  font-size: 15px;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
  background: #ffffff;
}
</style>
